import './index.scss';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Col, FormFeedback, Input, Label, Row } from 'reactstrap';

type PropsTypes = {
  form: UseFormReturn<any, object>;
  gymTourQuestion?: boolean;
};

export const MedicalFormPartA = (props: PropsTypes) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
  } = props.form;

  const medicalFormOptions = ['yes', 'no'];

  const isRequiredField = (field: string) => {
    if (!props.form.watch(`${field}`)) {
      return t('FORM_ERRORS_FIELD_IS_REQUIRED');
    }

    return true;
  };

  return (
    <div className="medical-declaration-part-a">
      {props.gymTourQuestion && (
        <Col md="12" className="form-switches form-user-input">
          <div className="form-check form-check-inputs">
            <Label for="didGymTour">{t('HAVE_YOU_DONE_GYM_TOUR_BEFORE')}</Label>
            <Controller
              name="didGymTour"
              control={control}
              render={({ field }) => (
                // @ts-ignore
                <Input
                  checked={props.form.getValues().didGymTour}
                  autoComplete="off"
                  id="didGymTour"
                  type="checkbox"
                  invalid={errors.didGymTour && true}
                  {...field}
                />
              )}
            />
            {errors.didGymTour ? (
              <FormFeedback className="form-input-error">
                {errors.didGymTour.message}
              </FormFeedback>
            ) : null}
          </div>
        </Col>
      )}
      <Row className="section-title">
        <Col md="6">
          <div>{t('MEDICAL_DECLARATION_PART_A_TITLE')}</div>
        </Col>
      </Row>
      <Row>
        <Col md="12" className="part-a-instructions">
          <div className="medical-declaration-paragraph-explain medical-form-label">
            {t('MEDICAL_DECLARATION_PART_A_EXPLAIN')}
          </div>
          <div className="part-a-instructions-titles part-a-instructions-titles-rtl">
            <span className="medical-form-bold-label">
              {t('MEDICAL_DECLARATION_YES_TITLE')}
            </span>
            <span className="medical-form-bold-label">
              {t('MEDICAL_DECLARATION_NO_TITLE')}
            </span>
          </div>
        </Col>
      </Row>
      <Row>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="heartDisease" className="medical-form-bold-label">
            {t('MEDICAL_DECLARATION_IS_HEART_DISEASE')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="heartDisease"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('heartDisease'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="heartDisease"
                        type="radio"
                        invalid={errors.heartDisease && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col md="12" className="medical-declaration-paragraph">
          <Label className="medical-form-bold-label">
            {t('MEDICAL_DECLARATION_IS_CHEST_PAIN')}
          </Label>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="restChestPain" className="medical-form-label">
            {t('MEDICAL_DECLARATION_IS_REST_CHEST_PAIN')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="restChestPain"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('restChestPain'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="restChestPain"
                        type="radio"
                        invalid={errors.restChestPain && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="dailyChestPain" className="medical-form-label">
            {t('MEDICAL_DECLARATION_IS_DAILY_CHEST_PAIN')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="dailyChestPain"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('dailyChestPain'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="dailyChestPain"
                        type="radio"
                        invalid={errors.dailyChestPain && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="exerciseChestPain" className="medical-form-label">
            {t('MEDICAL_DECLARATION_IS_EXERCISE_CHEST_PAIN')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="exerciseChestPain"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('exerciseChestPain'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="exerciseChestPain"
                        type="radio"
                        invalid={errors.exerciseChestPain && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col md="12" className="medical-declaration-paragraph">
          <Label className="medical-form-bold-label">
            {t('MEDICAL_DECLARATION_IN_THE_PAST_YEAR')}
          </Label>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="lostBalance" className="medical-form-label">
            {t('MEDICAL_DECLARATION_IS_LOST_BALANCE')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="lostBalance"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('lostBalance'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="lostBalance"
                        type="radio"
                        invalid={errors.lostBalance && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="lostConsciousness" className="medical-form-label">
            {t('MEDICAL_DECLARATION_IS_LOST_CONSCIOUSNESS')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="lostConsciousness"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('lostConsciousness'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="lostConsciousness"
                        type="radio"
                        invalid={errors.lostConsciousness && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col md="12" className="medical-declaration-paragraph">
          <Label className="medical-form-bold-label">
            {t('MEDICAL_DECLARATION_IS_ASTHMA_TITLE')}
          </Label>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="asthmaMedicalCare" className="medical-form-label">
            {t('MEDICAL_DECLARATION_IS_ASTHMA_MEDICAL_CARE')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="asthmaMedicalCare"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('asthmaMedicalCare'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="asthmaMedicalCare"
                        type="radio"
                        invalid={errors.asthmaMedicalCare && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="asthmaShortnessBreath" className="medical-form-label">
            {t('MEDICAL_DECLARATION_IS_ASTHMA_SHORTNESS_BREATH')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="asthmaShortnessBreath"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('asthmaShortnessBreath'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="asthmaShortnessBreath"
                        type="radio"
                        invalid={errors.asthmaShortnessBreath && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col md="12" className="medical-declaration-paragraph">
          <Label className="medical-form-bold-label">
            {t('MEDICAL_DECLARATION_IS_FAMILY_MEMBER_DEATH')}
          </Label>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="familyMemberHeartDisease" className="medical-form-label">
            {t('MEDICAL_DECLARATION_IS_FAMILY_MEMBER_HEART_DISEASE')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="familyMemberHeartDisease"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('familyMemberHeartDisease'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="familyMemberHeartDisease"
                        type="radio"
                        invalid={errors.familyMemberHeartDisease && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="familyMemberSuddenDeath" className="medical-form-label">
            {t('MEDICAL_DECLARATION_IS_FAMILY_MEMBER_SUDDEN_DEATH')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="familyMemberSuddenDeath"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('familyMemberSuddenDeath'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="familyMemberSuddenDeath"
                        type="radio"
                        invalid={errors.familyMemberSuddenDeath && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="trainingUnderMedical" className="medical-form-bold-label">
            {t('MEDICAL_DECLARATION_IS_TRAINING_UNDER_MEDICAL')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="trainingUnderMedical"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('trainingUnderMedical'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="trainingUnderMedical"
                        type="radio"
                        invalid={errors.trainingUnderMedical && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="chronicDisease" className="medical-form-bold-label">
            {t('MEDICAL_DECLARATION_IS_CHRONIC_DISEASE')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="chronicDisease"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('chronicDisease'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="chronicDisease"
                        type="radio"
                        invalid={errors.chronicDisease && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
        <Col
          md="12"
          className="medical-declaration-paragraph medical-declaration-radio-buttons"
        >
          <Label for="pregnancy" className="medical-form-bold-label">
            {t('MEDICAL_DECLARATION_IS_PREGNANCY')}
          </Label>
          <div className="medical-form-radio">
            <Controller
              name="pregnancy"
              control={control}
              rules={{
                validate: {
                  required: () => isRequiredField('pregnancy'),
                },
              }}
              render={({ field }) => {
                return (
                  <>
                    {medicalFormOptions.map((item) => (
                      <Input
                        id="pregnancy"
                        type="radio"
                        invalid={errors.pregnancy && true}
                        {...field}
                        value={item}
                      />
                    ))}
                  </>
                );
              }}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};
